import React, { useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import styled, { css } from 'styled-components'
import Input from 'components/atoms/Input'
import Select from 'components/atoms/Select'
import { useDispatch, useSelector } from 'react-redux'
import { getCurrentUserRole } from 'features/auth/ducks/selectors'
import { isNotNil, isNotNilOrEmpty } from 'utils/ramda'
import moment from 'moment'
import { propOr, pick, pathOr } from 'ramda'
import { selectFileCategories } from 'ducks/dictionaries/selectors'
import Collapse from '@mui/material/Collapse'
import InputDescription from 'features/createBp/components/atoms/InputDescription'
import EnhancedFileTypeSelector from 'features/bpDetails/Components/files/EnhancedFileTypeSelector'
import { updateUnitFileRoutine } from 'ducks/units/actions'
import { getFileTypes } from 'ducks/files/selectors'
import { getSelectedBp } from 'features/bpDetails/ducks/selectors'
import { updateFileRoutine } from 'ducks/files/actions'
import Checkbox from 'components/atoms/Checkbox'
import CloseIcon from '@mui/icons-material/Close'
import EditIcon from '@mui/icons-material/Edit'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import useBodyScrollLock from 'hooks/useBodyScrollLock'
import FileOnwardSharingField from 'features/bpDetails/Components/files/FileOnwardSharingField'

const emptyFileValues = {
  displayName: null,
  fileTypeId: null,
  floorNumbers: [],
  producedBy: null,
  creationDate: null,
  legalOwner: null,
  expirationDate: null,
  approvedBy: null,
  expirationReminderDate: null,
  segment: null,
  fileCategory: null,
  dateOfIssue: null
}

const EditFileModal = ({ open, floorOptions, onClose, file, isUnit, unit, bp: bpProp = null }) => {
  const modalContentRef = useRef(null)
  useBodyScrollLock(open, {
    allowSticky: true,
    allowScrollRef: modalContentRef
  })
  const dispatch = useDispatch()
  const [modalValues, setValues] = useState({})
  const fileTypes = useSelector(getFileTypes)
  const fileCategories = useSelector(selectFileCategories)
  const userRole = useSelector(getCurrentUserRole)
  const selectedBp = useSelector(getSelectedBp)
  const bp = bpProp ?? selectedBp
  const fileVersionId = pathOr('', ['versions', 0, 'id'], file)
  const [additionalOpen, setAdditionalOpen] = useState(false)
  const openedAtRef = useRef(0)
  const [wantsReminder, setWantsReminder] = useState(false)
  const [onwardSharingChange, setOnwardSharingChange] = useState(null)

  const toggleAdditionalInfo = () => setAdditionalOpen(prev => !prev)

  const handleValueChange = (name, value) => {
    if (name === 'expirationDate') {
      const subtractSixMonths = moment(value).subtract(6, 'month')
      const isBeforeToday = subtractSixMonths.isBefore(moment())
      const expirationReminderDate = isBeforeToday
        ? moment().add(1, 'day').format('YYYY-MM-DD')
        : subtractSixMonths.format('YYYY-MM-DD')

      setValues(prev => ({
        ...prev,
        [name]: value,
        expirationReminderDate
      }))
    } else {
      setValues(prev => ({ ...prev, [name]: value }))
    }
  }

  const handleCreationDateClear = () => {
    handleValueChange('creationDate', '')
  }

  const handleDateOfIssueClear = () => {
    handleValueChange('dateOfIssue', '')
  }

  const handleExpirationDateClear = () => {
    handleValueChange('expirationDate', '')
    handleValueChange('expirationReminderDate', '')
    setWantsReminder(false)
  }

  const handleReminderDateClear = () => {
    handleValueChange('expirationReminderDate', '')
  }

  const categoriesOptions = useMemo(() => {
    if (fileCategories) {
      const categories = fileCategories.map(type => ({
        label: type.name,
        value: type.id
      }))

      return [
        {
          label: 'All file types',
          value: 'all'
        },
        ...categories
      ]
    }
  }, [fileCategories, userRole])

  const typeOptions = useMemo(() => {
    if (fileTypes && modalValues.fileCategory) {
      const currentCategoryTypes =
        modalValues.fileCategory === 'all'
          ? fileTypes
          : fileTypes.filter(
            type => type.fileCategoryId === modalValues.fileCategory
          )

      const options = currentCategoryTypes.map(type => {
        const currentCategory = categoriesOptions.find(category => {
          return category.value === type.fileCategoryId
        })
        return {
          label: type.name,
          value: type.id,
          description: type.description,
          categoryName: propOr('', 'label', currentCategory),
          categoryId: propOr('', 'value', currentCategory)
        }
      })

      const properOptions = unit
        ? options.filter(option => option.label !== 'Building plan')
        : options

      return userRole !== 'emergency_service'
        ? properOptions.filter(option => option.label !== 'FRS floor plan')
        : properOptions
    } else {
      return []
    }
  }, [fileTypes, userRole, modalValues.fileCategory, categoriesOptions])

  const isFloorPlan = useMemo(() => {
    if (modalValues.fileTypeId) {
      const currentType = typeOptions.find(
        type => type.value === modalValues.fileTypeId
      )
      const label = propOr('', 'label', currentType)
      return label === 'FRS floor plan' || label === 'Building plan'
    }
  }, [modalValues.fileTypeId])

  useEffect(() => {
    const version = pathOr({}, ['versions', 0], file)
    if (file) {
      const expirationReminderDate = propOr(
        '',
        'expirationReminderDate',
        version
      )
        ? moment(propOr('', 'expirationReminderDate', version)).format(
          'YYYY-MM-DD'
        )
        : null

      setWantsReminder(isNotNilOrEmpty(expirationReminderDate))
      setOnwardSharingChange(null)

      setValues({
        ...pick(
          ['producedBy', 'legalOwner', 'approvedBy', 'segment', 'dateOfIssue'],
          version
        ),
        fileTypeId: pathOr('', ['fileType', 'id'], file),
        fileCategory: pathOr('', ['fileType', 'fileCategoryId'], file),
        displayName: pathOr('', ['displayName'], file),
        floorNumbers: pathOr([], ['floorNumbers'], file),
        expirationDate: propOr(null, 'expirationDate', version)
          ? moment(propOr('', 'expirationDate', version)).format('YYYY-MM-DD')
          : null,
        creationDate: propOr('', 'creationDate', version)
          ? moment(propOr(null, 'creationDate', version)).format('YYYY-MM-DD')
          : null,
        expirationReminderDate,
        dateOfIssue: propOr('', 'dateOfIssue', version)
          ? moment(propOr('', 'dateOfIssue', version)).format('YYYY-MM-DD')
          : null
      })
    }
  }, [file, open])

  // Auto-focus state for the file type selector
  const [shouldAutoFocus, setShouldAutoFocus] = useState(false)

  useEffect(() => {
    if (open) {
      setShouldAutoFocus(true)
    } else {
      setShouldAutoFocus(false)
    }
  }, [open])

  useEffect(() => {
    if (open) {
      openedAtRef.current = Date.now()
    }
  }, [open])

  const afterSubmitCallback = () => {
    onClose()
  }

  const handleUpload = e => {
    e.preventDefault()
    const onwardSharingBody = onwardSharingChange === null
      ? {}
      : { onwardSharingAllowed: onwardSharingChange }
    if (isUnit) {
      dispatch(
        updateUnitFileRoutine({
          bpId: bp.id,
          unitId: unit.id,
          fileId: file.id,
          fileVersionId,
          previousName: file.displayName || file.name || null,
          body: {
            ...modalValues,
            floorNumbers: isNotNil(modalValues.floorNumbers)
              ? modalValues.floorNumbers.map(num =>
                num === 'ground' ? '0' : num
              )
              : [],
            displayName: modalValues.displayName,
            expirationReminderDate: wantsReminder
              ? modalValues.expirationReminderDate
              : null,
            ...onwardSharingBody
          },
          clearValues: afterSubmitCallback
        })
      )
    } else {
      dispatch(
        updateFileRoutine({
          bpId: bp.id,
          id: file.id,
          fileVersionId,
          previousName: file.displayName || file.name || null,
          body: {
            ...modalValues,
            floorNumbers: isNotNil(modalValues.floorNumbers)
              ? modalValues.floorNumbers.map(num =>
                num === 'ground' ? '0' : num
              )
              : [],
            displayName: modalValues.displayName,
            expirationReminderDate: wantsReminder
              ? modalValues.expirationReminderDate
              : null,
            ...onwardSharingBody
          },
          clearValues: afterSubmitCallback
        })
      )
    }
  }

  useEffect(() => {
    if (!open) {
      setValues(emptyFileValues)
    }
  }, [open])


  const isValid = useMemo(() => {
    const { floorNumbers, fileTypeId, displayName } = modalValues
    return isFloorPlan
      ? [floorNumbers, displayName].every(e => isNotNilOrEmpty(e)) && isNotNilOrEmpty(fileTypeId)
      : isNotNilOrEmpty(displayName) && isNotNilOrEmpty(fileTypeId)
  }, [modalValues, isFloorPlan])

  const handleFileTypeSelect = (categoryId, typeId) => {
    setValues(prev => ({
      ...prev,
      fileTypeId: typeId,
      fileCategory: categoryId
    }))
    // Reset auto-focus after selection
    setShouldAutoFocus(false)
  }

  const searchTypeOptions = useMemo(() => {
    if (fileTypes) {
      const options = fileTypes.map(type => {
        const currentCategory = categoriesOptions.find(category => {
          return category.value === type.fileCategoryId
        })
        return {
          label: type.name,
          value: type.id,
          description: type.description,
          categoryName: propOr('', 'label', currentCategory),
          categoryId: propOr('', 'value', currentCategory)
        }
      })
      return userRole !== 'emergency_service'
        ? options.filter(option => option.label !== 'FRS floor plan')
        : options
    } else {
      return []
    }
  }, [fileTypes, userRole, categoriesOptions])

  if (!open) return null

  const handleBackdropClick = event => {
    const now = Date.now()
    if (now - openedAtRef.current < 500) return
    if (event.target !== event.currentTarget) return
    if (typeof document !== 'undefined' && document.body.dataset.fileTypeDropdownOpen === 'true') {
      return
    }
    onClose()
  }

  const modal = (
    <Backdrop onClick={handleBackdropClick}>
      <ModalContainer onClick={e => e.stopPropagation()}>
        <MobileHandle />

        <ModalHeader>
          <HeaderLeft>
            <HeaderIcon>
              <EditIcon />
            </HeaderIcon>
            <HeaderText>
              <HeaderTitle>Edit File Details</HeaderTitle>
              <HeaderFileName>{pathOr('Untitled', ['displayName'], file)}</HeaderFileName>
            </HeaderText>
          </HeaderLeft>
          <CloseButton onClick={onClose}>
            <CloseIcon />
          </CloseButton>
        </ModalHeader>

        {/* Content */}
        <ModalContent ref={modalContentRef}>
          <FormWrapper>
            <FormSection>
              <SectionLabel>File Type</SectionLabel>
              <FileTypeWrapper>
                <EnhancedFileTypeSelector
                  value={modalValues.fileTypeId}
                  onSelect={handleFileTypeSelect}
                  placeholder="Search and select file type..."
                  autoFocus={shouldAutoFocus}
                />
              </FileTypeWrapper>
            </FormSection>

            {isFloorPlan && (
              <FormSection>
                <Select
                  name='floorNumbers'
                  placeholder='Floor numbers'
                  options={floorOptions}
                  value={
                    isNotNil(modalValues.floorNumbers)
                      ? modalValues.floorNumbers
                      : []
                  }
                  multiple
                  label='Applicable floors'
                  onChange={handleValueChange}
                  noMargin
                />
                <HelperText>
                  Select the floor(s) that this file relates to or
                  <SelectAllButton
                    onClick={
                      floorOptions
                        ? () =>
                          handleValueChange(
                            'floorNumbers',
                            floorOptions.map(floor => floor.value)
                          )
                        : () => {}
                    }
                  >
                    select all
                  </SelectAllButton>
                </HelperText>
              </FormSection>
            )}

            <FormSection>
              <Input
                label='Title of file'
                value={modalValues.displayName}
                name='displayName'
                onChange={handleValueChange}
                placeholder='e.g. Fire Risk Assessment'
              />
            </FormSection>

            <FormSection>
              <Input
                label='Expiry date'
                value={modalValues.expirationDate}
                name='expirationDate'
                type='date'
                optional
                onChange={handleValueChange}
                additionalLabel={
                  isNotNilOrEmpty(modalValues.expirationDate) ? (
                    <ClearTrigger onClick={handleExpirationDateClear}>
                      clear
                    </ClearTrigger>
                  ) : null
                }
              />
            </FormSection>

            <FormSection>
              <CheckboxWrapper>
                <Checkbox
                  label='File expiry reminder'
                  name='wantsReminder'
                  disabled={!isNotNilOrEmpty(modalValues.expirationDate)}
                  value={wantsReminder}
                  onChange={() => setWantsReminder(prev => !prev)}
                />
              </CheckboxWrapper>
              <HelperText>
                Get reminded via email on a specific date that this file is due to
                expire. Every user with access to the Building Passport will
                receive this email.
              </HelperText>
            </FormSection>

            <FormSection>
              <Input
                label='Expiration reminder date'
                disabled={!wantsReminder}
                value={modalValues.expirationReminderDate}
                name='expirationReminderDate'
                type='date'
                onChange={handleValueChange}
                inputProps={{ min: moment().add(1, 'day').format('YYYY-MM-DD') }}
                additionalLabel={
                  isNotNilOrEmpty(modalValues.expirationReminderDate) ? (
                    <ClearTrigger onClick={handleReminderDateClear}>
                      clear
                    </ClearTrigger>
                  ) : null
                }
              />
            </FormSection>

            {file?.id && bp?.id && (
              <FormSection>
                <FileOnwardSharingField
                  bpId={bp.id}
                  fileId={file.id}
                  onChange={setOnwardSharingChange}
                />
              </FormSection>
            )}

            <AccordionSection>
              <AccordionHeader onClick={toggleAdditionalInfo}>
                <AccordionTitle>
                  Additional file information
                  <OptionalBadge>Optional</OptionalBadge>
                </AccordionTitle>
                <AccordionIcon open={additionalOpen}>
                  <ExpandMoreIcon />
                </AccordionIcon>
              </AccordionHeader>

              <Collapse in={additionalOpen}>
                <AccordionContent>
                  <AccordionDescription>
                    Add more details to achieve the highest quality Building Passport.
                  </AccordionDescription>

                  <Input
                    label='Legal Owner'
                    value={modalValues.legalOwner}
                    name='legalOwner'
                    onChange={handleValueChange}
                    placeholder='Name of the company'
                  />
                  <Input
                    label='Creator'
                    placeholder='Person who created the document'
                    value={modalValues.producedBy}
                    name='producedBy'
                    onChange={handleValueChange}
                  />
                  <Input
                    type='date'
                    label='Date of issue'
                    value={modalValues.dateOfIssue}
                    name='dateOfIssue'
                    onChange={handleValueChange}
                    additionalLabel={
                      isNotNilOrEmpty(modalValues.dateOfIssue) ? (
                        <ClearTrigger onClick={handleDateOfIssueClear}>
                          clear
                        </ClearTrigger>
                      ) : null
                    }
                  />
                  <Input
                    type='date'
                    label='Creation date'
                    value={modalValues.creationDate}
                    name='creationDate'
                    onChange={handleValueChange}
                    inputProps={{ max: moment().format('YYYY-MM-DD') }}
                    additionalLabel={
                      isNotNilOrEmpty(modalValues.creationDate) ? (
                        <ClearTrigger onClick={handleCreationDateClear}>
                          clear
                        </ClearTrigger>
                      ) : null
                    }
                  />
                  <Input
                    label='Approved by'
                    value={modalValues.approvedBy}
                    name='approvedBy'
                    onChange={handleValueChange}
                    placeholder='Person who approved the document'
                  />
                  <Input
                    label='Segment / sheet'
                    value={modalValues.segment}
                    name='segment'
                    onChange={handleValueChange}
                    placeholder='Enter if applicable'
                  />
                </AccordionContent>
              </Collapse>
            </AccordionSection>
          </FormWrapper>
        </ModalContent>

        <ModalFooter>
          <CancelButton onClick={onClose}>Cancel</CancelButton>
          <SaveButton onClick={handleUpload} disabled={!isValid}>
            Save Changes
          </SaveButton>
        </ModalFooter>
      </ModalContainer>
    </Backdrop>
  )

  if (typeof document === 'undefined') return modal

  return createPortal(modal, document.body)
}

export default EditFileModal

const Backdrop = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2147483647;
  animation: fadeIn 0.2s ease;

  @media (max-width: 768px) {
    align-items: flex-end;
  }

  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`

const ModalContainer = styled.div`
  background: white;
  border-radius: 16px;
  width: 90vw;
  max-width: 550px;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  overflow: hidden;

  @media (max-width: 768px) {
    width: 100%;
    max-width: 100%;
    max-height: 92vh;
    border-radius: 20px 20px 0 0;
    animation: slideUp 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }

  @keyframes slideUp {
    from { transform: translateY(100%); }
    to { transform: translateY(0); }
  }
`

const MobileHandle = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: block;
    width: 36px;
    height: 4px;
    background: #d1d5db;
    border-radius: 2px;
    margin: 10px auto 6px;
    flex-shrink: 0;
  }
`

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid #f3f4f6;
  flex-shrink: 0;

  @media (max-width: 768px) {
    padding: 12px 16px;
  }
`

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  min-width: 0;
`

const HeaderIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: linear-gradient(135deg, #34bdcc 0%, #2da8b6 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  svg {
    color: white;
    font-size: 22px;
  }

  @media (max-width: 768px) {
    width: 36px;
    height: 36px;
    border-radius: 8px;

    svg {
      font-size: 20px;
    }
  }
`

const HeaderText = styled.div`
  flex: 1;
  min-width: 0;
`

const HeaderTitle = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: #1f2937;

  @media (max-width: 768px) {
    font-size: 16px;
  }
`

const HeaderFileName = styled.div`
  font-size: 13px;
  color: #6b7280;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-top: 2px;

  @media (max-width: 768px) {
    font-size: 12px;
  }
`

const CloseButton = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: none;
  background: #f3f4f6;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  transition: all 0.2s;

  svg {
    font-size: 20px;
    color: #6b7280;
  }

  &:hover {
    background: #e5e7eb;
  }

  &:active {
    transform: scale(0.95);
  }
`

const ModalContent = styled.div`
  flex: 1;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;

  @media (max-width: 768px) {
    padding-bottom: 8px;
  }
`

const FormWrapper = styled.div`
  padding: 20px 24px;

  @media (max-width: 768px) {
    padding: 16px;
  }
`

const FormSection = styled.div`
  margin-bottom: 16px;

  @media (max-width: 768px) {
    margin-bottom: 14px;
  }
`

const SectionLabel = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 8px;

  @media (max-width: 768px) {
    font-size: 13px;
  }
`

const FileTypeWrapper = styled.div`
`

const HelperText = styled.div`
  font-size: 12px;
  color: #6b7280;
  margin-top: 6px;
  line-height: 1.4;

  @media (max-width: 768px) {
    font-size: 11px;
  }
`

const SelectAllButton = styled.span`
  color: #34bdcc;
  cursor: pointer;
  font-weight: 600;
  margin-left: 4px;

  &:hover {
    text-decoration: underline;
  }
`

const CheckboxWrapper = styled.div`
`

const ClearTrigger = styled.div`
  cursor: pointer;
  color: #34bdcc;
  font-size: 12px;
  font-weight: 600;

  &:hover {
    text-decoration: underline;
  }
`

const AccordionSection = styled.div`
  margin-top: 20px;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  overflow: hidden;

  @media (max-width: 768px) {
    margin-top: 16px;
    border-radius: 10px;
  }
`

const AccordionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  background: #f9fafb;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: #f3f4f6;
  }

  @media (max-width: 768px) {
    padding: 12px 14px;
  }
`

const AccordionTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
  color: #374151;

  @media (max-width: 768px) {
    font-size: 13px;
  }
`

const OptionalBadge = styled.span`
  font-size: 11px;
  font-weight: 500;
  color: #9ca3af;
  background: #e5e7eb;
  padding: 2px 8px;
  border-radius: 10px;
`

const AccordionIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s;
  transform: rotate(${({ open }) => (open ? '180deg' : '0')});
  color: #6b7280;

  svg {
    font-size: 20px;
  }
`

const AccordionContent = styled.div`
  padding: 16px;
  border-top: 1px solid #e5e7eb;

  @media (max-width: 768px) {
    padding: 14px;
  }
`

const AccordionDescription = styled.div`
  font-size: 13px;
  color: #6b7280;
  margin-bottom: 16px;
  line-height: 1.4;

  @media (max-width: 768px) {
    font-size: 12px;
    margin-bottom: 14px;
  }
`

const ModalFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 24px;
  border-top: 1px solid #f3f4f6;
  background: #f9fafb;
  flex-shrink: 0;

  @media (max-width: 768px) {
    padding: 12px 16px;
    padding-bottom: calc(12px + env(safe-area-inset-bottom));
    gap: 10px;
  }
`

const CancelButton = styled.button`
  padding: 10px 20px;
  border-radius: 8px;
  border: 1px solid #d1d5db;
  background: white;
  color: #6b7280;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #f9fafb;
    border-color: #9ca3af;
  }

  &:active {
    transform: scale(0.98);
  }

  @media (max-width: 768px) {
    flex: 1;
    padding: 12px 16px;
    font-size: 13px;
  }
`

const SaveButton = styled.button`
  padding: 10px 24px;
  border-radius: 8px;
  border: none;
  background: ${({ disabled }) => disabled ? '#d1d5db' : '#34bdcc'};
  color: white;
  font-size: 14px;
  font-weight: 600;
  cursor: ${({ disabled }) => disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background: #2da8b6;
  }

  &:active:not(:disabled) {
    transform: scale(0.98);
  }

  @media (max-width: 768px) {
    flex: 1;
    padding: 12px 16px;
    font-size: 13px;
  }
`
